import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Delete, CornerDownLeft, ChevronUp, X } from "lucide-react";
import { http } from "@/lib/api";

/**
 * Touchscreen on-screen keyboard with Greek + Latin layouts.
 *
 * Designed for waiters in environments without a physical keyboard.
 * Supports lowercase / SHIFT / Greek-Latin toggle, basic editing keys,
 * and emits the final string on submit.
 */
const ROWS = {
  el: {
    lower: [
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
      [";", "ε", "ρ", "τ", "υ", "θ", "ι", "ο", "π"],
      ["α", "σ", "δ", "φ", "γ", "η", "ξ", "κ", "λ"],
      ["ζ", "χ", "ψ", "ω", "β", "ν", "μ", ",", ".", "-"],
    ],
    upper: [
      ["!", "@", "#", "$", "%", "&", "*", "(", ")", "_"],
      [":", "Ε", "Ρ", "Τ", "Υ", "Θ", "Ι", "Ο", "Π"],
      ["Α", "Σ", "Δ", "Φ", "Γ", "Η", "Ξ", "Κ", "Λ"],
      ["Ζ", "Χ", "Ψ", "Ω", "Β", "Ν", "Μ", ",", ".", "-"],
    ],
  },
  en: {
    lower: [
      ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
      ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
      ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
      ["z", "x", "c", "v", "b", "n", "m", ",", ".", "-"],
    ],
    upper: [
      ["!", "@", "#", "$", "%", "&", "*", "(", ")", "_"],
      ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
      ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
      ["Z", "X", "C", "V", "B", "N", "M", ",", ".", "-"],
    ],
  },
};

export const OnScreenKeyboard = ({
  open,
  initialValue = "",
  title = "Πληκτρολόγιο",
  onConfirm,
  onClose,
  maxLength = 120,
  // Pass a string key (e.g. "restaurant-freetext") to enable persistent
  // dictionary suggestions. When ``suggestionsBackend`` is true the list is
  // pulled from / persisted to the backend (shared across all stations);
  // otherwise localStorage is used (per-device, offline-only).
  suggestionsKey = null,
  suggestionsBackend = false,
  maxSuggestions = 20,
}) => {
  const [text, setText] = useState(initialValue);
  const [lang, setLang] = useState("el");
  const [shift, setShift] = useState(false);
  const [suggestions, setSuggestions] = useState([]); // [{id?, text}]

  const loadFromBackend = useCallback(async () => {
    if (!suggestionsKey) return;
    try {
      const r = await http.get(
        `/free-text-suggestions?category=${encodeURIComponent(suggestionsKey)}&limit=${maxSuggestions}`,
      );
      setSuggestions(Array.isArray(r.data) ? r.data : []);
    } catch {
      setSuggestions([]);
    }
  }, [suggestionsKey, maxSuggestions]);

  const loadFromLocalStorage = useCallback(() => {
    if (!suggestionsKey) return;
    try {
      const raw = localStorage.getItem(`osk-sug-${suggestionsKey}`);
      const list = raw ? JSON.parse(raw) : [];
      setSuggestions(list.map((t) => ({ text: t })));
    } catch {
      setSuggestions([]);
    }
  }, [suggestionsKey]);

  // Load suggestions when opening.
  useEffect(() => {
    if (open) {
      setText(initialValue || "");
      setShift(false);
      if (suggestionsKey) {
        if (suggestionsBackend) loadFromBackend();
        else loadFromLocalStorage();
      }
    }
  }, [open, initialValue, suggestionsKey, suggestionsBackend, loadFromBackend, loadFromLocalStorage]);

  const saveSuggestion = useCallback(
    async (val) => {
      if (!suggestionsKey || !val || !val.trim()) return;
      const cleaned = val.trim();
      if (suggestionsBackend) {
        try {
          await http.post("/free-text-suggestions", {
            category: suggestionsKey,
            text: cleaned,
          });
        } catch {
          /* network failure — swallow, UX continues */
        }
        return;
      }
      // localStorage fallback
      try {
        const raw = localStorage.getItem(`osk-sug-${suggestionsKey}`);
        const list = raw ? JSON.parse(raw) : [];
        const next = [cleaned, ...list.filter((x) => x.toLowerCase() !== cleaned.toLowerCase())].slice(
          0,
          maxSuggestions,
        );
        localStorage.setItem(`osk-sug-${suggestionsKey}`, JSON.stringify(next));
      } catch {
        /* ignore */
      }
    },
    [suggestionsKey, suggestionsBackend, maxSuggestions],
  );

  const removeSuggestion = async (s) => {
    if (!suggestionsKey) return;
    if (suggestionsBackend && s.id) {
      try {
        await http.delete(`/free-text-suggestions/${s.id}`);
      } catch {
        /* ignore */
      }
      setSuggestions((cur) => cur.filter((x) => x.id !== s.id));
      return;
    }
    // localStorage path — compare by text.
    const next = suggestions.filter((x) => x.text !== s.text);
    setSuggestions(next);
    try {
      localStorage.setItem(
        `osk-sug-${suggestionsKey}`,
        JSON.stringify(next.map((x) => x.text)),
      );
    } catch {
      /* ignore */
    }
  };

  const press = useCallback(
    (ch) => {
      setText((t) => (t.length >= maxLength ? t : t + ch));
      if (shift) setShift(false);
    },
    [shift, maxLength],
  );

  const backspace = () => setText((t) => t.slice(0, -1));
  const clear = () => setText("");
  const space = () => setText((t) => (t.length >= maxLength ? t : t + " "));
  const submit = () => {
    saveSuggestion(text);
    onConfirm?.(text);
  };

  const rows = ROWS[lang][shift ? "upper" : "lower"];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl p-0 gap-0 bg-slate-50" data-testid="osk-dialog">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-white">
          <div className="font-bold text-slate-800">{title}</div>
          <button
            type="button"
            onClick={onClose}
            data-testid="osk-close"
            className="w-9 h-9 grid place-items-center rounded-md hover:bg-slate-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Display */}
        <div className="px-5 py-3 bg-white border-b border-slate-200">
          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, maxLength))}
            data-testid="osk-display"
            className="w-full h-14 px-4 text-2xl font-medium border-2 border-blue-400 rounded-lg bg-blue-50 focus:outline-none focus:border-blue-600"
            autoFocus
          />
          <div className="mt-1 flex justify-between text-xs text-slate-500">
            <span>{text.length}/{maxLength}</span>
            <button
              type="button"
              onClick={clear}
              data-testid="osk-clear"
              className="text-red-600 hover:underline"
            >
              Καθαρισμός
            </button>
          </div>
        </div>

        {/* Suggestion pills (recent / frequent free-text entries) */}
        {suggestionsKey && suggestions.length > 0 && (
          <div className="px-3 py-2 bg-amber-50 border-b border-amber-200" data-testid="osk-suggestions">
            <div className="text-[10px] uppercase tracking-wider text-amber-900 font-bold mb-1.5 px-1">
              Πρόσφατα σχόλια — πάτησε για συμπλήρωση
            </div>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.slice(0, 12).map((s) => (
                <div
                  key={s.id || s.text}
                  className="group inline-flex items-center bg-white border border-amber-300 rounded-full pl-3 pr-1 py-1 shadow-sm hover:bg-amber-100 transition-colors"
                >
                  <button
                    type="button"
                    onClick={() => setText(s.text.slice(0, maxLength))}
                    data-testid="osk-suggestion-pill"
                    className="text-sm font-medium text-slate-700 max-w-[220px] truncate"
                  >
                    {s.text}
                    {s.usage_count > 1 && (
                      <span className="ml-1.5 text-[10px] font-mono text-amber-700 bg-amber-100 rounded px-1 align-middle">
                        ×{s.usage_count}
                      </span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSuggestion(s);
                    }}
                    data-testid="osk-suggestion-remove"
                    title="Διαγραφή πρότασης"
                    className="ml-1 w-5 h-5 grid place-items-center rounded-full text-slate-400 hover:bg-red-100 hover:text-red-600"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Keys */}
        <div className="px-3 py-4 flex flex-col gap-2">
          {rows.map((row, ri) => (
            <div key={ri} className="flex gap-1.5 justify-center">
              {/* Indent middle rows like a real keyboard */}
              {ri === 2 && <div className="w-5" />}
              {row.map((ch) => (
                <button
                  key={ch}
                  type="button"
                  onClick={() => press(ch)}
                  data-testid={`osk-key-${ch}`}
                  className="w-[64px] h-14 rounded-lg bg-white border border-slate-300 text-lg font-medium shadow-sm hover:bg-blue-50 active:scale-95 active:bg-blue-100 transition-all"
                >
                  {ch}
                </button>
              ))}
            </div>
          ))}

          {/* Bottom action row */}
          <div className="flex gap-1.5 justify-center mt-1">
            <button
              type="button"
              onClick={() => setShift((s) => !s)}
              data-testid="osk-shift"
              className={`h-14 px-4 rounded-lg border text-sm font-bold uppercase transition-all flex items-center gap-1 ${
                shift ? "bg-blue-600 text-white border-blue-700" : "bg-white border-slate-300 hover:bg-slate-100"
              }`}
            >
              <ChevronUp size={18} /> Shift
            </button>
            <button
              type="button"
              onClick={() => setLang((l) => (l === "el" ? "en" : "el"))}
              data-testid="osk-lang"
              className="h-14 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-100 text-sm font-bold uppercase"
            >
              {lang === "el" ? "EN" : "ΕΛ"}
            </button>
            <button
              type="button"
              onClick={space}
              data-testid="osk-space"
              className="h-14 flex-1 max-w-xl rounded-lg border border-slate-300 bg-white hover:bg-blue-50 active:scale-[0.98] text-sm font-bold uppercase"
            >
              ── Space ──
            </button>
            <button
              type="button"
              onClick={backspace}
              data-testid="osk-backspace"
              className="h-14 px-4 rounded-lg border border-slate-300 bg-white hover:bg-red-50 text-sm font-bold flex items-center gap-1"
            >
              <Delete size={18} /> Διαγραφή
            </button>
            <button
              type="button"
              onClick={submit}
              data-testid="osk-submit"
              className="h-14 px-6 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold uppercase flex items-center gap-1 shadow"
            >
              <CornerDownLeft size={18} /> OK
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnScreenKeyboard;
